/** @format */
import React, { FunctionComponent, useEffect, useState } from 'react';
import GridContainer from '../../../../components/Grid/GridContainer';
import GridItem from '../../../../components/Grid/GridItem';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import CustomTextButtom from '../../../../components/Buttons/CustomTextButton';
import { useDispatch } from 'react-redux';
import { ServicesAction } from '../../../../modules/features/FeaturesReducers';
import I18n from '../../../../components/Translate/I18n';
import CustomInputExt from '../../../../components/CustomInput/CustomInputExt';
import { SimpleSelect } from '../../../../components/Adescom/SimpleSelect';
import { useSimCardServiceGroupHeader } from '../../../../modules/lib/hooks/useSimCardServiceGroupList';
import { FeatureTypes } from '../../../../modules/features/FeaturesTypes';
import { ServicesMvneType, useOperatorType, useServiceType, useServiceUseType } from '../../../../modules/lib/hooks/useOperatorType';
import { SimCardServiceTypeItem } from '../../../../interfaces/collections/SimCardServiceInterface';
import { ItemLoaders } from '../../../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../../../helpers/FormGeneral';
import Button from '@material-ui/core/Button';
import { ReactComponent as Cancel } from '../../../../assets/themes/OPL/img/icons/Cancel.svg';
import { BmeForm } from './SimCardServiceTypeForm/BmeFrom';
import { MappedComponentForm } from './SimCardServiceTypeForm/MappedComponentForm';
import { SdgForm } from './SimCardServiceTypeForm/SdgForm';

const _ = require('underscore');

export type OplSimCardServiceTypeItemForm = {
    serviceUseType: any;
    simCardServiceTypeStack: any;
    onChangeSelectEvent: any;
    onChangeEvent: any;
    operatorType: any;
    groupList: any;
};

type OplSimCardServiceTypeItemEditType = {
    simCardServiceType: SimCardServiceTypeItem;
    serviceGroupList: any;
    openEdit: boolean;
    close: () => void;
};

export const OplSimCardServiceTypeItemEdit: FunctionComponent<OplSimCardServiceTypeItemEditType> = ({
    simCardServiceType,
    serviceGroupList,
    openEdit,
    close,
}): React.ReactElement => {
    const dispatch = useDispatch();
    const [simCardServiceTypeStack, setSimCardServiceTypeStack] = useState<SimCardServiceTypeItem | null | undefined>(simCardServiceType);
    const featureOperatorType = new FeatureTypes(process.env);
    const operatorType = useOperatorType(featureOperatorType);
    const serviceTypes = useServiceType(featureOperatorType);
    const groupList = useSimCardServiceGroupHeader(serviceGroupList);

    const serviceUseType = useServiceUseType();

    useEffect(() => {
        if (!simCardServiceTypeStack || simCardServiceType.id !== simCardServiceTypeStack.id) {
            setSimCardServiceTypeStack(simCardServiceType);
        }
    }, [simCardServiceType, simCardServiceTypeStack]);

    const confirmCallback = (): void => {
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.putWithCallback(
            FormGeneralHelper.getByUri(simCardServiceTypeStack?.['@id'] ?? 'empty'),
            ServicesAction.FEATURES_LOAD_SIM_CARD_SERVICE_TYPE_RELOAD,
            simCardServiceTypeStack,
            (data: SimCardServiceTypeItem): void => {
                close();
            },
            { dispatchAfterCallback: false, propagateDataToCallback: true }
        );
    };

    const onChangeSelectEvent = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const current = _.clone(simCardServiceTypeStack);
        const field = event.target.name;
        const selected = event.target.value;
        switch (field) {
            case 'visibleFlag':
            case 'syncDetails':
            case 'mccCheck':
            case 'allowWaitingForActivation':
            case 'zoneCheck':
            case 'syncUsage':
            case 'singleShotFlag':
            case 'generateCdr':
                setSimCardServiceTypeStack({ ...current, [field]: parseInt(selected) === 1 });
                break;
            case 'serviceType':
                setSimCardServiceTypeStack({ ...current, [field]: featureOperatorType.mapToString(parseInt(selected)) });
                break;
            case 'cycle':
            case 'cdrOnAmount':
                setSimCardServiceTypeStack({ ...current, [field]: parseInt(selected) === 1 ? 1 : -1 });
                break;
            default:
                setSimCardServiceTypeStack({ ...current, [field]: selected });
                break;
        }
    };

    const onChangeEvent = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const current = _.clone(simCardServiceTypeStack);
        let value: string | number | null = event.target.value;
        const field = event.target.name;
        if (field === 'externalSystemId') {
            value = parseInt(value);
        }
        setSimCardServiceTypeStack({ ...current, [field]: value });
    };

    const getServiceTypeId = (serviceTypeName: string): number => {
        let id = -2;
        for (let i = 0; i < serviceTypes.length; i++) {
            if (serviceTypeName === serviceTypes[i].name) {
                return serviceTypes[i].value;
            }
        }
        return id;
    };

    return (
        <Dialog
            open={openEdit ?? false}
            maxWidth="sm"
            onClose={close}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            {simCardServiceType && simCardServiceTypeStack && (
                <React.Fragment>
                    <DialogTitle>
                        <GridContainer justifyContent={'space-between'}>
                            <GridItem xs={11}>
                                <h4>
                                    <I18n trans="prov:feature.edit_component_title" valueObject={{ service_name: simCardServiceType.description }} />
                                </h4>
                            </GridItem>
                            <GridItem xs={1}>
                                <Button onClick={close} color="primary">
                                    <Cancel width={24} height={24} />
                                </Button>
                            </GridItem>
                        </GridContainer>
                    </DialogTitle>
                    <DialogContent>
                        <GridContainer spacing={1}>
                            {simCardServiceType.serviceType === ServicesMvneType.BME && (
                                <BmeForm
                                    serviceUseType={serviceUseType}
                                    simCardServiceTypeStack={simCardServiceTypeStack}
                                    onChangeSelectEvent={onChangeSelectEvent}
                                    onChangeEvent={onChangeEvent}
                                    operatorType={operatorType}
                                    groupList={groupList}
                                />
                            )}
                            {simCardServiceType.serviceType === ServicesMvneType.SDG && (
                                <SdgForm
                                    serviceUseType={serviceUseType}
                                    simCardServiceTypeStack={simCardServiceTypeStack}
                                    onChangeSelectEvent={onChangeSelectEvent}
                                    onChangeEvent={onChangeEvent}
                                    operatorType={operatorType}
                                    groupList={groupList}
                                />
                            )}
                            {simCardServiceType.serviceType === ServicesMvneType.MCBME && (
                                <MappedComponentForm
                                    serviceUseType={serviceUseType}
                                    simCardServiceTypeStack={simCardServiceTypeStack}
                                    onChangeSelectEvent={onChangeSelectEvent}
                                    onChangeEvent={onChangeEvent}
                                    operatorType={operatorType}
                                    groupList={groupList}
                                />
                            )}
                        </GridContainer>
                    </DialogContent>
                </React.Fragment>
            )}
            <DialogActions>
                <CustomTextButtom callback={confirmCallback} callbackParam={{}} label="prov:feature.confirm_title" size="sm" />
            </DialogActions>
        </Dialog>
    );
};
